import { driver } from "driver.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiTour } from "../api/tours";

// Shared product-tour helper around Driver.js, backend-driven.
// Each page calls useTour({ tourKey, steps }) and gets { startTour, showButton }.
// The tour auto-fires on the user's first view (recorded server-side via
// apiTour), and the "Take the tour" button stays visible for TOUR_BUTTON_WINDOW_MS
// (4 weeks) from that first view so staff can replay it, then hides.
//
// enabled: gate the whole tour off for users who shouldn't see it (e.g. a
// manager-only tour on a page non-managers can also reach).
// No localStorage — user prefs live on the backend (see AGENTS.md
// "User preferences: backend, not browser-local").
//
// steps: Driver.js DriveStep[] ({ element: cssSelector, popover: { title, description, side?, align? } }).
// Memoize `steps` at the call site so the auto-fire timer isn't reset on every render.

const TOUR_BUTTON_WINDOW_MS = 4 * 7 * 24 * 60 * 60 * 1000; // 4 weeks

export function useTour({ tourKey, steps, enabled = true, startDelayMs = 300 }) {
  const [firstView, setFirstView] = useState(null); // Date | null
  const [ready, setReady] = useState(false);
  const driverRef = useRef(null);
  const autoTimerRef = useRef(null);

  // Load first-view from the backend; on a genuine first view, mark it (server time, once).
  useEffect(() => {
    if (!enabled) { return; } // disabled for users who shouldn't see this tour
    let cancelled = false;
    apiTour.getFirstView(tourKey)
      .then((res) => {
        if (cancelled) { return; }
        const fv = res.data.result?.firstView;
        setFirstView(fv ? new Date(fv) : null);
        setReady(true);
        if (!fv) { apiTour.markFirstView(tourKey).catch(() => {}); }
      })
      .catch(() => { /* leave ready=false: don't auto-tour a user we can't confirm */ });
    return () => { cancelled = true; };
  }, [tourKey, enabled]);

  const startTour = useCallback(() => {
    if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = null; }
    if (driverRef.current) { driverRef.current.destroy(); }
    const d = driver({
      showProgress: true,
      allowClose: true,
      smoothScroll: true,
      steps,
    });
    driverRef.current = d;
    d.drive();
  }, [steps, tourKey]);

  const seen = !!firstView;
  const showButton = enabled && (!firstView || Date.now() - firstView.getTime() < TOUR_BUTTON_WINDOW_MS);

  // Auto-fire only on the genuine first view (ready && not seen).
  useEffect(() => {
    if (!ready || seen) { return; }
    autoTimerRef.current = setTimeout(startTour, startDelayMs);
    return () => {
      if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = null; }
    };
  }, [ready, seen, startTour, startDelayMs, tourKey]);

  // Tear down an active tour if the page unmounts mid-tour, so the overlay doesn't linger.
  useEffect(() => () => { driverRef.current?.destroy(); }, []);

  return { startTour, showButton };
}